import { useMemo, useState, type InputHTMLAttributes } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler, type UseFormRegisterReturn } from "react-hook-form";
import { z } from "zod";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  ApiError,
  getAdminEmailSettings,
  getAdminSettings,
  sendAdminEmailTest,
  syncAdminAuth0EmailProvider,
  updateAdminEmailSettings,
  updateAdminSettings,
  uploadAdminSettingsAsset,
} from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { coerceBool } from "@/lib/coerce";
import type { AdminEmailPayload, AdminSettingsPayload } from "@/lib/types";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * M8c H1 / H3 / H4 / H5 / H6 / H7 — `/admin/settings`.
 *
 * Branding (16 fields) + Downloads toggle + Asset uploads (logo /
 * favicon) + Email/Notification card (editable provider/base URL/from
 * fields/write-only Brevo key + test-send). Audit feed reads the same
 * payload's `audit` field.
 *
 * **H2 build-time decision (locked):** the three live-streaming
 * fields (`live_enabled`, `live_rtmp_public_url`,
 * `live_offline_message`) are NOT rendered here. A one-line
 * pointer directs operators to `/admin/live`. This avoids the
 * legacy bug where a partial Settings save could clobber Live
 * config — and removes a confusing dual-edit surface.
 *
 * Save flow (H6): partial PUT — only changed keys go to the
 * backend. The backend echoes the updated settings + an audit
 * array. We invalidate to refetch + the audit feed updates
 * automatically.
 */

/** Branding fields, in their inventory-locked order. */
const BRANDING_FIELDS: Array<{
  key: string;
  label: string;
  group: "club" | "nav" | "filters" | "stats" | "game" | "downloads";
  kind?: "text" | "textarea";
}> = [
  // `app_name` is fixed at "tktic" (the product name) and is not
  // operator-editable; the bundled wordmark in `web/public/brand/` is
  // the brand surface. The legacy `main_team_name` field was removed
  // in M12+ — tktic is multi-team now and matches carry their own
  // team_id, so there is no single "main team" to filter against.
  // Club (formerly "Season" — the title field doubles as the club name
  // rendered next to the club logo in the public season header.)
  { key: "season_title", label: "Club title", group: "club" },
  { key: "season_intro", label: "Club intro", group: "club", kind: "textarea" },
  // Nav
  { key: "nav_matches_label", label: "Nav · Matches label", group: "nav" },
  { key: "nav_admin_label", label: "Nav · Admin label", group: "nav" },
  // Filters
  { key: "filter_all_label", label: "Filter · All", group: "filters" },
  { key: "filter_home_label", label: "Filter · Home", group: "filters" },
  { key: "filter_away_label", label: "Filter · Away", group: "filters" },
  // Stats
  { key: "stat_matches_label", label: "Stat · Matches", group: "stats" },
  { key: "stat_ready_label", label: "Stat · Ready", group: "stats" },
  { key: "stat_processing_label", label: "Stat · Processing", group: "stats" },
  // Game-page labels
  { key: "game_back_label", label: "Game · Back", group: "game" },
  { key: "game_replay_label", label: "Game · Match replay label", group: "game" },
  {
    key: "game_video_status_label",
    label: "Game · Video status",
    group: "game",
  },
  { key: "download_label", label: "Download label", group: "downloads" },
];

const BRANDING_GROUP_LABELS: Record<
  (typeof BRANDING_FIELDS)[number]["group"],
  string
> = {
  club: "Club",
  nav: "Navigation",
  filters: "Filters",
  stats: "Stats tiles",
  game: "Game page",
  downloads: "Downloads",
};

const emailSettingsSchema = z.object({
  email_provider: z.enum(["disabled", "brevo"]),
  email_public_base_url: z.string(),
  email_from: z.string().email("Enter a valid sender email").or(z.literal("")),
  email_from_name: z.string(),
  email_brevo_api_key: z.string(),
  clear_brevo_api_key: z.boolean(),
  auth0_email_provider: z.enum(["disabled", "brevo_smtp"]),
  auth0_email_from: z.string().email("Enter a valid Auth0 sender email").or(z.literal("")),
  auth0_email_from_name: z.string(),
  auth0_dev_email_from: z.string().email("Enter a valid dev sender email").or(z.literal("")),
  auth0_dev_email_from_name: z.string(),
  auth0_dev_client_id: z.string(),
  auth0_prod_email_from: z.string().email("Enter a valid prod sender email").or(z.literal("")),
  auth0_prod_email_from_name: z.string(),
  auth0_prod_client_id: z.string(),
  auth0_brevo_smtp_host: z.string(),
  auth0_brevo_smtp_port: z.string().regex(/^\d*$/, "Use a numeric SMTP port"),
  auth0_brevo_smtp_user: z.string(),
  auth0_brevo_smtp_key: z.string(),
  clear_auth0_brevo_smtp_key: z.boolean(),
});

type EmailSettingsValues = z.infer<typeof emailSettingsSchema>;

const emailTestSchema = z.object({
  email: z.string().email("Enter a valid recipient email"),
});

type EmailTestValues = z.infer<typeof emailTestSchema>;

function defaultAuth0EmailPayload(): AdminEmailPayload["auth0_email"] {
  return {
    configured: false,
    settings: {
      auth0_email_provider: "disabled",
      auth0_email_from: "",
      auth0_email_from_name: "Tktic",
      auth0_dev_email_from: "",
      auth0_dev_email_from_name: "",
      auth0_dev_client_id: "",
      auth0_prod_email_from: "",
      auth0_prod_email_from_name: "",
      auth0_prod_client_id: "",
      auth0_brevo_smtp_host: "smtp-relay.brevo.com",
      auth0_brevo_smtp_port: "587",
      auth0_brevo_smtp_user: "",
    },
    status: {
      has_from: false,
      has_brevo_smtp_user: false,
      has_brevo_smtp_key: false,
      brevo_smtp_key_source: "",
      smtp_user_source: "",
      provider_source: "default",
    },
    sync: {
      last_synced_at: "",
      last_synced_by: "",
      last_status: "",
      last_error: "",
      needs_sync: false,
      can_sync: false,
    },
  };
}

export function AdminSettingsPage() {
  const settingsQuery = useQuery({
    queryKey: unscopedKey("admin-settings"),
    queryFn: getAdminSettings,
  });

  return (
    <div data-testid="admin-settings" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Platform"
        title="Settings"
        description="Manage branding, public assets, downloads, outbound email, and platform audit context."
        meta={settingsQuery.isFetching ? "Refreshing…" : undefined}
      />

      {settingsQuery.error ? (
        <p
          data-testid="admin-settings-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load settings.
        </p>
      ) : null}

      {/* H2 — pointer instead of editable fields. */}
      <div
        data-testid="admin-settings-live-pointer"
        className="rounded border border-rule bg-surface shadow-sm p-3 text-xs"
      >
        <p className="text-ink">
          <span className="text-ink-muted">Live config:</span> manage live
          streaming key, RTMP URL, and offline message on{" "}
          <a
            href="/admin/live"
            data-testid="admin-settings-live-link"
            className="text-accent hover:underline"
          >
            /admin/live
          </a>
          .
        </p>
      </div>

      {settingsQuery.data ? (
        <>
          <BrandingCard settings={settingsQuery.data} />
          <DownloadsCard settings={settingsQuery.data} />
          <AssetsCard settings={settingsQuery.data} />
          <EmailCard />
          <AuditCard audit={settingsQuery.data.audit ?? []} />
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// H1 — Branding card (+ H6 partial-save flow)
// ---------------------------------------------------------------------------

function BrandingCard({ settings }: { settings: AdminSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = useMemo(
    () =>
      Object.keys(draft).filter(
        (k) => draft[k] !== (live[k] ?? "") && draft[k] !== undefined,
      ),
    [draft, live],
  );

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      updateAdminSettings(payload),
    onSuccess: async () => {
      // Invalidate both the admin payload AND the public app-settings
      // cache so the public shell/header picks up branding changes
      // (e.g. `season_title`) immediately instead of waiting for the
      // 30 s staleTime to expire. Mirrors the wizard's invalidation
      // flow in WelcomeShell.TeamStep.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("app-settings"),
        }),
      ]);
      setDraft({});
      setFieldErrors({});
      toast.success("Settings saved");
    },
    onError: (err) => {
      toast.error("Couldn't save settings", { description: formatErr(err) });
      if (err instanceof ApiError && err.status === 400) {
        // Per-key 400 from the backend's `errors` map.
        const detail = err.detail as unknown;
        if (
          detail &&
          typeof detail === "object" &&
          "errors" in (detail as Record<string, unknown>)
        ) {
          const errs = (detail as { errors: Record<string, string> }).errors;
          setFieldErrors(errs);
          setSubmitError("Some fields failed validation.");
          return;
        }
      }
      setSubmitError(formatErr(err));
    },
  });

  const grouped = useMemo(() => {
    const out: Record<string, typeof BRANDING_FIELDS> = {};
    for (const f of BRANDING_FIELDS) {
      (out[f.group] ||= []).push(f);
    }
    return out;
  }, []);

  return (
    <section
      data-testid="admin-settings-branding"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Branding
        </h3>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setFieldErrors({});
            const payload = Object.fromEntries(dirty.map((k) => [k, draft[k]]));
            save.mutate(payload);
          }}
          disabled={dirty.length === 0 || save.isPending}
          data-testid="admin-settings-branding-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending
            ? "Saving…"
            : `Save${dirty.length ? ` (${dirty.length})` : ""}`}
        </button>
      </header>

      {submitError ? (
        <p
          data-testid="admin-settings-branding-error"
          className="text-destructive font-mono text-xs"
        >
          {submitError}
        </p>
      ) : null}

      {Object.entries(grouped).map(([group, fields]) => (
        <div key={group} className="flex flex-col gap-2">
          <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {BRANDING_GROUP_LABELS[group as keyof typeof BRANDING_GROUP_LABELS]}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <BrandingField
                key={f.key}
                field={f}
                currentValue={
                  draft[f.key] !== undefined
                    ? draft[f.key]
                    : String(live[f.key] ?? "")
                }
                originalValue={String(live[f.key] ?? "")}
                error={fieldErrors[f.key]}
                onChange={(v) =>
                  setDraft((d) => ({ ...d, [f.key]: v }))
                }
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function BrandingField({
  field,
  currentValue,
  originalValue,
  error,
  onChange,
}: {
  field: (typeof BRANDING_FIELDS)[number];
  currentValue: string;
  originalValue: string;
  error: string | undefined;
  onChange: (v: string) => void;
}) {
  const dirty = currentValue !== originalValue;
  return (
    <label
      className="flex flex-col gap-1 text-xs"
      data-testid={`admin-settings-field-${field.key}`}
      data-dirty={dirty ? "true" : undefined}
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {field.label}
      </span>
      {field.kind === "textarea" ? (
        <textarea
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          data-testid={`admin-settings-input-${field.key}`}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      ) : (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`admin-settings-input-${field.key}`}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      )}
      {error ? (
        <span
          data-testid={`admin-settings-error-${field.key}`}
          className="text-destructive text-[0.6rem] font-mono"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// H3 — Downloads toggle
// ---------------------------------------------------------------------------

function DownloadsCard({ settings }: { settings: AdminSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const currentEnabled = coerceBool(live.downloads_enabled);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (next: boolean) =>
      updateAdminSettings({ downloads_enabled: next ? "1" : "0" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-settings"),
      });
      toast.success("Settings saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save settings", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="admin-settings-downloads"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <h3 className="text-sm font-display uppercase tracking-wider text-ink">
        Downloads
      </h3>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mut.mutate(!currentEnabled);
        }}
        disabled={mut.isPending}
        data-testid="admin-settings-downloads-toggle"
        data-active={currentEnabled ? "true" : undefined}
        className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {currentEnabled ? "Downloads enabled" : "Downloads disabled"}
      </button>
      {error ? (
        <p
          data-testid="admin-settings-downloads-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// H4 — Asset uploads
// ---------------------------------------------------------------------------

function AssetsCard({ settings }: { settings: AdminSettingsPayload }) {
  return (
    <section
      data-testid="admin-settings-assets"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <h3 className="text-sm font-display uppercase tracking-wider text-ink">
        App assets
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <AssetRow
          kind="club_logo"
          label="Club logo"
          previewUrl={settings.assets?.club_logo_url ?? null}
        />
        <AssetRow
          kind="favicon"
          label="Favicon"
          previewUrl={settings.assets?.favicon_url ?? null}
        />
      </div>
    </section>
  );
}

function AssetRow({
  kind,
  label,
  previewUrl,
}: {
  kind: "club_logo" | "favicon";
  label: string;
  previewUrl: string | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (file: File) => uploadAdminSettingsAsset(kind, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-settings"),
      });
      toast.success(`${label} uploaded`);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error(`Couldn't upload ${label.toLowerCase()}`, {
        description: formatErr(err),
      });
    },
  });

  return (
    <div
      data-testid={`admin-settings-asset-${kind}`}
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <div className="flex items-center gap-2">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`Current ${label.toLowerCase()}`}
            data-testid={`admin-settings-asset-${kind}-preview`}
            className="h-10 w-10 object-contain rounded border border-rule bg-canvas"
          />
        ) : (
          <div
            data-testid={`admin-settings-asset-${kind}-no-preview`}
            className="h-10 w-10 rounded border border-dashed border-rule text-[0.55rem] flex items-center justify-center text-ink-muted"
          >
            none
          </div>
        )}
        <input
          type="file"
          accept={kind === "favicon" ? ".ico,.png,.svg" : ".png,.jpg,.jpeg,.svg,.webp"}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setError(null);
            mut.mutate(file);
            // Reset the input so picking the same file twice still
            // fires onChange.
            e.target.value = "";
          }}
          disabled={mut.isPending}
          data-testid={`admin-settings-asset-${kind}-input`}
          className="text-xs text-ink-muted file:rounded file:border file:border-rule file:bg-surface file:text-ink file:px-2 file:py-0.5 file:text-[0.6rem] file:font-display file:uppercase file:tracking-wider"
        />
      </div>
      {mut.isPending ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Uploading…</p>
      ) : null}
      {error ? (
        <p
          data-testid={`admin-settings-asset-${kind}-error`}
          className="text-destructive font-mono text-[0.6rem]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// H5 / PR E — Email / Notification card (editable + test-send)
// ---------------------------------------------------------------------------

function EmailCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-email-settings"),
    queryFn: getAdminEmailSettings,
  });

  return (
    <section
      data-testid="admin-settings-email"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Email / Notifications
          </h3>
          <p className="text-[0.65rem] text-ink-muted">
            Configure Brevo delivery and send a one-off test without exposing the stored API key.
          </p>
        </div>
        <span className="text-[0.55rem] font-display uppercase tracking-wider text-accent">
          Editable
        </span>
      </header>
      {query.isLoading ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Loading…</p>
      ) : query.error ? (
        <p
          data-testid="admin-settings-email-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(query.error)}
        </p>
      ) : query.data ? (
        <>
          <EmailReadView payload={query.data} />
          <EmailSettingsForm
            payload={query.data}
            onSaved={async () => {
              await queryClient.invalidateQueries({
                queryKey: unscopedKey("admin-email-settings"),
              });
            }}
          />
          <Auth0EmailSyncPanel
            payload={query.data}
            onSynced={async () => {
              await queryClient.invalidateQueries({
                queryKey: unscopedKey("admin-email-settings"),
              });
            }}
          />
          <EmailTestForm />
        </>
      ) : null}
    </section>
  );
}

function emailDefaults(payload: AdminEmailPayload): EmailSettingsValues {
  const auth0Email = payload.auth0_email ?? defaultAuth0EmailPayload();
  return {
    email_provider: payload.settings.email_provider === "brevo" ? "brevo" : "disabled",
    email_public_base_url: payload.settings.email_public_base_url ?? "",
    email_from: payload.settings.email_from ?? "",
    email_from_name: payload.settings.email_from_name ?? "Tktic",
    email_brevo_api_key: "",
    clear_brevo_api_key: false,
    auth0_email_provider: auth0Email?.settings.auth0_email_provider === "brevo_smtp" ? "brevo_smtp" : "disabled",
    auth0_email_from: auth0Email?.settings.auth0_email_from ?? "",
    auth0_email_from_name: auth0Email?.settings.auth0_email_from_name ?? "Tktic",
    auth0_dev_email_from: auth0Email?.settings.auth0_dev_email_from ?? "",
    auth0_dev_email_from_name: auth0Email?.settings.auth0_dev_email_from_name ?? "",
    auth0_dev_client_id: auth0Email?.settings.auth0_dev_client_id ?? "",
    auth0_prod_email_from: auth0Email?.settings.auth0_prod_email_from ?? "",
    auth0_prod_email_from_name: auth0Email?.settings.auth0_prod_email_from_name ?? "",
    auth0_prod_client_id: auth0Email?.settings.auth0_prod_client_id ?? "",
    auth0_brevo_smtp_host: auth0Email?.settings.auth0_brevo_smtp_host ?? "smtp-relay.brevo.com",
    auth0_brevo_smtp_port: auth0Email?.settings.auth0_brevo_smtp_port ?? "587",
    auth0_brevo_smtp_user: auth0Email?.settings.auth0_brevo_smtp_user ?? "",
    auth0_brevo_smtp_key: "",
    clear_auth0_brevo_smtp_key: false,
  };
}

function EmailSettingsForm({
  payload,
  onSaved,
}: {
  payload: AdminEmailPayload;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<EmailSettingsValues>({
    resolver: zodResolver(emailSettingsSchema),
    values: emailDefaults(payload),
  });
  const clearKey = form.watch("clear_brevo_api_key");
  const clearAuth0SmtpKey = form.watch("clear_auth0_brevo_smtp_key");

  const save = useMutation({
    mutationFn: updateAdminEmailSettings,
    onSuccess: async (next) => {
      form.reset(emailDefaults(next));
      setSubmitError(null);
      setSubmitMessage("Email settings saved.");
      toast.success("Email settings saved");
      await onSaved();
    },
    onError: (err) => {
      setSubmitMessage(null);
      setSubmitError(formatErr(err));
      toast.error("Couldn't save email settings", {
        description: formatErr(err),
      });
    },
  });

  const onSubmit: SubmitHandler<EmailSettingsValues> = (values) => {
    setSubmitError(null);
    setSubmitMessage(null);
    const body = {
      email_provider: values.email_provider,
      email_public_base_url: values.email_public_base_url.trim(),
      email_from: values.email_from.trim(),
      email_from_name: values.email_from_name.trim(),
      ...(values.clear_brevo_api_key
        ? { clear_brevo_api_key: true }
        : values.email_brevo_api_key.trim()
          ? { email_brevo_api_key: values.email_brevo_api_key.trim() }
          : {}),
      auth0_email_provider: values.auth0_email_provider,
      auth0_email_from: values.auth0_email_from.trim(),
      auth0_email_from_name: values.auth0_email_from_name.trim(),
      auth0_dev_email_from: values.auth0_dev_email_from.trim(),
      auth0_dev_email_from_name: values.auth0_dev_email_from_name.trim(),
      auth0_dev_client_id: values.auth0_dev_client_id.trim(),
      auth0_prod_email_from: values.auth0_prod_email_from.trim(),
      auth0_prod_email_from_name: values.auth0_prod_email_from_name.trim(),
      auth0_prod_client_id: values.auth0_prod_client_id.trim(),
      auth0_brevo_smtp_host: values.auth0_brevo_smtp_host.trim(),
      auth0_brevo_smtp_port: values.auth0_brevo_smtp_port.trim(),
      auth0_brevo_smtp_user: values.auth0_brevo_smtp_user.trim(),
      ...(values.clear_auth0_brevo_smtp_key
        ? { clear_auth0_brevo_smtp_key: true }
        : values.auth0_brevo_smtp_key.trim()
          ? { auth0_brevo_smtp_key: values.auth0_brevo_smtp_key.trim() }
          : {}),
    };
    save.mutate(body);
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs"
    >
      <div className="md:col-span-2 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        Tktic transactional email
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          App provider
        </span>
        <Controller
          control={form.control}
          name="email_provider"
          render={({ field }) => (
            <TkticSelect
              value={field.value ?? "disabled"}
              onChange={field.onChange}
              data-testid="admin-settings-email-provider-input"
              ariaLabel="Email provider"
              size="sm"
              options={[
                { value: "disabled", label: "Disabled" },
                { value: "brevo", label: "Brevo" },
              ]}
            />
          )}
        />
      </label>
      <EmailTextField
        label="Public base URL"
        testid="admin-settings-email-base-url-input"
        registration={form.register("email_public_base_url")}
        error={form.formState.errors.email_public_base_url?.message}
        placeholder="https://tktic.com"
      />
      <EmailTextField
        label="From address"
        testid="admin-settings-email-from-input"
        registration={form.register("email_from")}
        error={form.formState.errors.email_from?.message}
        placeholder="noreply@tktic.com"
        type="email"
      />
      <EmailTextField
        label="From name"
        testid="admin-settings-email-from-name-input"
        registration={form.register("email_from_name")}
        error={form.formState.errors.email_from_name?.message}
        placeholder="Tktic"
      />
      <EmailTextField
        label="New Brevo API key"
        testid="admin-settings-email-brevo-key-input"
        registration={form.register("email_brevo_api_key")}
        error={form.formState.errors.email_brevo_api_key?.message}
        placeholder={payload.status.has_brevo_api_key ? "Leave blank to keep current key" : "Paste new Brevo API key"}
        type="password"
        autoComplete="new-password"
        disabled={clearKey}
      />
      <button
        type="button"
        onClick={() => form.setValue("clear_brevo_api_key", !clearKey)}
        data-testid="admin-settings-email-clear-key"
        data-active={clearKey ? "true" : undefined}
        className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
      >
        {clearKey ? "Clear Brevo key ✓" : "Clear saved Brevo key"}
      </button>
      <div className="md:col-span-2 mt-2 border-t border-rule pt-3 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        Auth0 authentication email
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Auth0 provider
        </span>
        <Controller
          control={form.control}
          name="auth0_email_provider"
          render={({ field }) => (
            <TkticSelect
              value={field.value ?? "disabled"}
              onChange={field.onChange}
              data-testid="admin-settings-auth0-email-provider-input"
              ariaLabel="Auth0 email provider"
              size="sm"
              options={[
                { value: "disabled", label: "Disabled" },
                { value: "brevo_smtp", label: "Brevo SMTP" },
              ]}
            />
          )}
        />
      </label>
      <EmailTextField
        label="Default Auth0 from address"
        testid="admin-settings-auth0-email-from-input"
        registration={form.register("auth0_email_from")}
        error={form.formState.errors.auth0_email_from?.message}
        placeholder="noreply@tktic.com"
        type="email"
      />
      <EmailTextField
        label="Default Auth0 from name"
        testid="admin-settings-auth0-email-from-name-input"
        registration={form.register("auth0_email_from_name")}
        error={form.formState.errors.auth0_email_from_name?.message}
        placeholder="Tktic"
      />
      <EmailTextField
        label="Dev from address"
        testid="admin-settings-auth0-dev-email-from-input"
        registration={form.register("auth0_dev_email_from")}
        error={form.formState.errors.auth0_dev_email_from?.message}
        placeholder="dev-noreply@tktic.com"
        type="email"
      />
      <EmailTextField
        label="Dev from name"
        testid="admin-settings-auth0-dev-email-from-name-input"
        registration={form.register("auth0_dev_email_from_name")}
        error={form.formState.errors.auth0_dev_email_from_name?.message}
        placeholder="Tktic Dev"
      />
      <EmailTextField
        label="Dev Auth0 client ID"
        testid="admin-settings-auth0-dev-client-id-input"
        registration={form.register("auth0_dev_client_id")}
        error={form.formState.errors.auth0_dev_client_id?.message}
        placeholder="Tktic Dev application client ID"
      />
      <EmailTextField
        label="Prod from address"
        testid="admin-settings-auth0-prod-email-from-input"
        registration={form.register("auth0_prod_email_from")}
        error={form.formState.errors.auth0_prod_email_from?.message}
        placeholder="noreply@tktic.com"
        type="email"
      />
      <EmailTextField
        label="Prod from name"
        testid="admin-settings-auth0-prod-email-from-name-input"
        registration={form.register("auth0_prod_email_from_name")}
        error={form.formState.errors.auth0_prod_email_from_name?.message}
        placeholder="Tktic"
      />
      <EmailTextField
        label="Prod Auth0 client ID"
        testid="admin-settings-auth0-prod-client-id-input"
        registration={form.register("auth0_prod_client_id")}
        error={form.formState.errors.auth0_prod_client_id?.message}
        placeholder="Tktic Prod application client ID"
      />
      <EmailTextField
        label="SMTP host"
        testid="admin-settings-auth0-smtp-host-input"
        registration={form.register("auth0_brevo_smtp_host")}
        error={form.formState.errors.auth0_brevo_smtp_host?.message}
        placeholder="smtp-relay.brevo.com"
      />
      <EmailTextField
        label="SMTP port"
        testid="admin-settings-auth0-smtp-port-input"
        registration={form.register("auth0_brevo_smtp_port")}
        error={form.formState.errors.auth0_brevo_smtp_port?.message}
        placeholder="587"
        inputMode="numeric"
      />
      <EmailTextField
        label="SMTP user"
        testid="admin-settings-auth0-smtp-user-input"
        registration={form.register("auth0_brevo_smtp_user")}
        error={form.formState.errors.auth0_brevo_smtp_user?.message}
        placeholder="Brevo SMTP login"
      />
      <EmailTextField
        label="New SMTP key"
        testid="admin-settings-auth0-smtp-key-input"
        registration={form.register("auth0_brevo_smtp_key")}
        error={form.formState.errors.auth0_brevo_smtp_key?.message}
        placeholder={(payload.auth0_email ?? defaultAuth0EmailPayload()).status.has_brevo_smtp_key ? "Leave blank to keep current key" : "Paste new Brevo SMTP key"}
        type="password"
        autoComplete="new-password"
        disabled={clearAuth0SmtpKey}
      />
      <button
        type="button"
        onClick={() => form.setValue("clear_auth0_brevo_smtp_key", !clearAuth0SmtpKey)}
        data-testid="admin-settings-auth0-smtp-clear-key"
        data-active={clearAuth0SmtpKey ? "true" : undefined}
        className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
      >
        {clearAuth0SmtpKey ? "Clear SMTP key ✓" : "Clear saved SMTP key"}
      </button>
      <div className="md:col-span-2 flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          data-testid="admin-settings-email-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? "Saving…" : "Save email settings"}
        </button>
        {submitMessage ? (
          <span data-testid="admin-settings-email-save-status" className="text-[0.65rem] font-mono text-good">
            {submitMessage}
          </span>
        ) : null}
        {submitError ? (
          <span data-testid="admin-settings-email-save-error" className="text-[0.65rem] font-mono text-destructive">
            {submitError}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function EmailTextField({
  label,
  testid,
  registration,
  error,
  placeholder,
  type = "text",
  autoComplete,
  inputMode,
  disabled,
}: {
  label: string;
  testid: string;
  registration: UseFormRegisterReturn;
  error?: string;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <input
        type={type}
        {...registration}
        data-testid={testid}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        disabled={disabled}
        className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {error ? <span className="text-destructive text-[0.6rem] font-mono">{error}</span> : null}
    </label>
  );
}

function Auth0EmailSyncPanel({
  payload,
  onSynced,
}: {
  payload: AdminEmailPayload;
  onSynced: () => Promise<void>;
}) {
  const toast = useToast();
  const sync = (payload.auth0_email ?? defaultAuth0EmailPayload()).sync;
  const auth0Email = payload.auth0_email ?? defaultAuth0EmailPayload();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: syncAdminAuth0EmailProvider,
    onSuccess: async () => {
      setError(null);
      setMessage("Auth0 email settings synced.");
      toast.success("Auth0 email settings synced");
      await onSynced();
    },
    onError: (err) => {
      const detail = formatErr(err);
      setMessage(null);
      setError(detail);
      toast.error("Couldn't sync Auth0 email settings", { description: detail });
    },
  });

  return (
    <div className="rounded border border-rule bg-elevated px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Auth0 email sync
          </p>
          <p data-testid="admin-settings-auth0-sync-status" className="text-[0.65rem] font-mono text-ink-muted">
            {sync.needs_sync
              ? "Saved settings need sync"
              : sync.last_status === "success"
                ? `Synced${sync.last_synced_at ? ` ${sync.last_synced_at}` : ""}`
                : sync.last_status === "failed"
                  ? "Last sync failed"
                  : "Not synced yet"}
          </p>
          {sync.last_error ? (
            <p data-testid="admin-settings-auth0-sync-last-error" className="text-[0.65rem] font-mono text-destructive">
              {sync.last_error}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="admin-settings-auth0-sync"
          disabled={mutation.isPending || !auth0Email.sync.can_sync}
          onClick={() => mutation.mutate()}
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? "Syncing…" : "Sync Auth0 email"}
        </button>
      </div>
      {message ? (
        <p data-testid="admin-settings-auth0-sync-message" className="mt-2 text-[0.65rem] font-mono text-good">
          {message}
        </p>
      ) : null}
      {error ? (
        <p data-testid="admin-settings-auth0-sync-error" className="mt-2 text-[0.65rem] font-mono text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function EmailTestForm() {
  const toast = useToast();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<EmailTestValues>({
    resolver: zodResolver(emailTestSchema),
    defaultValues: { email: "" },
  });
  const sendTest = useMutation({
    mutationFn: sendAdminEmailTest,
    onSuccess: (response) => {
      setError(null);
      setResult(
        response.ok
          ? `Test email sent${response.message_id ? ` · ${response.message_id}` : ""}`
          : `Test email ${response.status}: ${response.detail || response.provider}`,
      );
      if (response.ok) {
        toast.success("Test email sent");
      } else {
        toast.error("Couldn't send test email", {
          description: `${response.status}: ${response.detail || response.provider}`,
        });
      }
    },
    onError: (err) => {
      setResult(null);
      setError(formatErr(err));
      toast.error("Couldn't send test email", { description: formatErr(err) });
    },
  });

  const onSubmit: SubmitHandler<EmailTestValues> = (values) => {
    setResult(null);
    setError(null);
    sendTest.mutate({ email: values.email.trim() });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-test-form"
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Send test email
        </span>
        <input
          type="email"
          {...form.register("email")}
          data-testid="admin-settings-email-test-input"
          placeholder="operator@example.test"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {form.formState.errors.email?.message ? (
          <span className="text-destructive text-[0.6rem] font-mono">
            {form.formState.errors.email.message}
          </span>
        ) : null}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={sendTest.isPending}
          data-testid="admin-settings-email-test-send"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sendTest.isPending ? "Sending…" : "Send test"}
        </button>
        {result ? (
          <span data-testid="admin-settings-email-test-result" className="text-[0.65rem] font-mono text-good">
            {result}
          </span>
        ) : null}
        {error ? (
          <span data-testid="admin-settings-email-test-error" className="text-[0.65rem] font-mono text-destructive">
            {error}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function EmailReadView({ payload }: { payload: AdminEmailPayload }) {
  const { settings, status, provider, configured } = payload;
  const auth0Email = payload.auth0_email ?? defaultAuth0EmailPayload();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
      <div className="md:col-span-2 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        Tktic transactional email
      </div>
      <Stat
        label="App provider"
        value={provider}
        testid="admin-settings-email-provider"
        sub={`source: ${status.provider_source}`}
      />
      <Stat
        label="Configured"
        value={configured ? "Yes" : "No"}
        testid="admin-settings-email-configured"
        tone={configured ? "good" : "muted"}
      />
      <Stat
        label="Public base URL"
        value={settings.email_public_base_url || "—"}
        testid="admin-settings-email-base-url"
        tone={status.has_public_base_url ? "muted" : "warn"}
      />
      <Stat
        label="From address"
        value={settings.email_from || "—"}
        testid="admin-settings-email-from"
        tone={status.has_from ? "muted" : "warn"}
      />
      <Stat
        label="From name"
        value={settings.email_from_name || "—"}
        testid="admin-settings-email-from-name"
      />
      <Stat
        label="Brevo API key"
        value={status.has_brevo_api_key ? "Configured" : "Not set"}
        testid="admin-settings-email-brevo-key"
        sub={
          status.has_brevo_api_key ? `source: ${status.brevo_api_key_source}` : ""
        }
        tone={status.has_brevo_api_key ? "good" : "warn"}
      />
      <div className="md:col-span-2 mt-2 border-t border-rule pt-3 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        Auth0 authentication email
      </div>
      <Stat
        label="Auth0 provider"
        value={auth0Email.settings.auth0_email_provider}
        testid="admin-settings-auth0-email-provider"
        sub={`source: ${auth0Email.status.provider_source}`}
      />
      <Stat
        label="Auth0 configured"
        value={auth0Email.configured ? "Yes" : "No"}
        testid="admin-settings-auth0-email-configured"
        tone={auth0Email.configured ? "good" : "muted"}
      />
      <Stat
        label="Default Auth0 from address"
        value={auth0Email.settings.auth0_email_from || "—"}
        testid="admin-settings-auth0-email-from"
        tone={auth0Email.status.has_from ? "muted" : "warn"}
      />
      <Stat
        label="Default Auth0 from name"
        value={auth0Email.settings.auth0_email_from_name || "—"}
        testid="admin-settings-auth0-email-from-name"
      />
      <Stat
        label="Dev sender"
        value={
          auth0Email.settings.auth0_dev_email_from
            ? `${auth0Email.settings.auth0_dev_email_from_name || auth0Email.settings.auth0_email_from_name || "Tktic"} <${auth0Email.settings.auth0_dev_email_from}>`
            : "default"
        }
        testid="admin-settings-auth0-dev-email-from"
      />
      <Stat
        label="Dev Auth0 client"
        value={auth0Email.settings.auth0_dev_client_id || "name fallback"}
        testid="admin-settings-auth0-dev-client-id"
        tone={auth0Email.settings.auth0_dev_client_id ? "muted" : "warn"}
      />
      <Stat
        label="Prod sender"
        value={
          auth0Email.settings.auth0_prod_email_from
            ? `${auth0Email.settings.auth0_prod_email_from_name || auth0Email.settings.auth0_email_from_name || "Tktic"} <${auth0Email.settings.auth0_prod_email_from}>`
            : "default"
        }
        testid="admin-settings-auth0-prod-email-from"
      />
      <Stat
        label="Prod Auth0 client"
        value={auth0Email.settings.auth0_prod_client_id || "not set"}
        testid="admin-settings-auth0-prod-client-id"
      />
      <Stat
        label="SMTP host"
        value={`${auth0Email.settings.auth0_brevo_smtp_host || "—"}:${auth0Email.settings.auth0_brevo_smtp_port || "—"}`}
        testid="admin-settings-auth0-smtp-host"
      />
      <Stat
        label="SMTP user"
        value={auth0Email.status.has_brevo_smtp_user ? "Configured" : "Not set"}
        testid="admin-settings-auth0-smtp-user"
        sub={auth0Email.status.has_brevo_smtp_user ? `source: ${auth0Email.status.smtp_user_source}` : ""}
        tone={auth0Email.status.has_brevo_smtp_user ? "good" : "warn"}
      />
      <Stat
        label="SMTP key"
        value={auth0Email.status.has_brevo_smtp_key ? "Configured" : "Not set"}
        testid="admin-settings-auth0-smtp-key"
        sub={auth0Email.status.has_brevo_smtp_key ? `source: ${auth0Email.status.brevo_smtp_key_source}` : ""}
        tone={auth0Email.status.has_brevo_smtp_key ? "good" : "warn"}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  testid,
  tone = "muted",
}: {
  label: string;
  value: string;
  sub?: string;
  testid: string;
  tone?: "muted" | "good" | "warn";
}) {
  const toneClass =
    tone === "good"
      ? "text-good"
      : tone === "warn"
        ? "text-accent"
        : "text-ink";
  return (
    <div
      data-testid={testid}
      data-tone={tone}
      className="flex flex-col rounded border border-rule bg-elevated p-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`text-xs font-mono ${toneClass}`}>{value}</span>
      {sub ? (
        <span className="text-[0.6rem] font-mono text-ink-muted">{sub}</span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// H7 — Audit feed (controlled expander)
// ---------------------------------------------------------------------------

function AuditCard({
  audit,
}: {
  audit: NonNullable<AdminSettingsPayload["audit"]>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="admin-settings-audit"
      data-expander-open={open ? "true" : "false"}
      className="rounded border border-rule bg-surface shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="admin-settings-audit-toggle"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm"
      >
        <span className="font-display uppercase tracking-wider text-ink">
          Settings audit
        </span>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {audit.length} {open ? "▼" : "▶"}
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3">
          {audit.length === 0 ? (
            <p
              data-testid="admin-settings-audit-empty"
              className="text-[0.6rem] font-mono text-ink-muted"
            >
              No settings changes recorded.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {audit.map((a, i) => (
                <li
                  key={`${a.timestamp}-${a.key}-${i}`}
                  data-testid="admin-settings-audit-row"
                  className="rounded border border-rule/60 bg-elevated px-2 py-1 text-[0.6rem] font-mono"
                >
                  <span className="text-ink-muted">{a.timestamp}</span>{" "}
                  <span className="text-ink">{a.key}</span>{" "}
                  <span className="text-ink-muted">by {a.actor}</span>{" "}
                  <span className="text-ink-muted">
                    · {JSON.stringify(a.old_value)} → {JSON.stringify(a.new_value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

// coerceBool moved to `web/src/lib/coerce.ts` (M11 lift — public viewer + admin share).

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.detail && typeof err.detail === "object" && "message" in err.detail) {
      const msg = (err.detail as { message?: unknown }).message;
      if (typeof msg === "string") return msg;
    }
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
